import { createFileRoute, Link } from '@tanstack/react-router';
import { z } from 'zod';

import { AdminPageHeader } from '@/components/admin/admin-page-header';
import { AuditReportDetail } from '@/components/admin/audit-reports/audit-report-detail';
import { ApproveAuditReportButton } from '@/components/admin/audit-reports/approve-audit-report-button';
import { RejectAuditReportDialog } from '@/components/admin/audit-reports/reject-audit-report-dialog';
import { MarkSentAuditReportButton } from '@/components/admin/audit-reports/mark-sent-audit-report-button';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useAdminAuditReport } from '@/hooks/data/admin/use-audit-reports';
import { ROUTES } from '@/constants/routes';

const paramSchema = z.object({ intakeId: z.string().uuid() });

export const Route = createFileRoute(
  '/_authenticated/admin/audit-reports/$intakeId',
)({
  staticData: { title: 'Audit report' },
  parseParams: (params) => paramSchema.parse(params),
  component: AdminAuditReportDetailPage,
});

function DetailSkeleton(): React.ReactElement {
  return (
    <div className="grid gap-4 px-4 py-6 sm:px-8 @3xl:grid-cols-2">
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton variant="card" key={i} className="h-12 rounded-xl" />
      ))}
    </div>
  );
}

function AdminAuditReportDetailPage(): React.ReactElement {
  const { intakeId } = Route.useParams();
  const reportQuery = useAdminAuditReport(intakeId);

  const backLink = (
    <Button variant="outline" asChild>
      <Link to={ROUTES.ADMIN_AUDIT_REPORTS}>Back</Link>
    </Button>
  );

  if (reportQuery.isLoading) {
    return (
      <main>
        <AdminPageHeader title="Audit report" actions={backLink} />
        <DetailSkeleton />
      </main>
    );
  }

  if (reportQuery.isError || !reportQuery.data) {
    return (
      <main>
        <AdminPageHeader title="Audit report" actions={backLink} />
        <div className="px-4 py-12 text-center sm:px-8">
          <p className="text-fg-3 text-fs-13">
            Failed to load submission. Please try again.
          </p>
        </div>
      </main>
    );
  }

  const report = reportQuery.data;

  return (
    <main>
      <AdminPageHeader
        title={report.brand}
        description={report.website}
        actions={
          <>
            {backLink}
            <RejectAuditReportDialog report={report} />
            <ApproveAuditReportButton report={report} />
            <MarkSentAuditReportButton report={report} />
          </>
        }
      />
      <AuditReportDetail report={report} />
    </main>
  );
}
